<template>
    <div>
        <portal to="navbar-list">
            <navbar :site-slug="siteSlug" :menus="menus"></navbar>
        </portal>
        <portal to="cart">
            <cart :site-slug="siteSlug" :cart="cart" :cart-type="menuType"></cart>
        </portal>

        <section-selector :sections="sections" v-on:click-on-section="goToSection" :menu-type="menuType" :menus="menus" :current-section="activeSectionId || 0" v-on:change-menu="goToMenu" v-on:click-on-filters="goToFilters" />

        <section :class="`${menuType}-menu-section`">
            <div class="container">
                <div v-if="inactiveMessage" class="small text-danger my-3">{{ inactiveMessage }}</div>

                <div class="row" v-if="activeFilters">
                    <div class="col-sm-12">
                        <div class="filters-list border-0">
                            <p>{{ activeFilters }} {{ $t("active filters") }}</p>
                            <ul class="list-inline">
                                <li v-for="tag in tags" :key="tag.id"><button type="button" class="btn btn-link p-0" v-on:click="goToFilters">
                                        <div class="tags">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48">
                                                <path d="M0 0h24v24H0z" fill="none" />
                                                <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z" />
                                            </svg>
                                            {{ tag.name }}
                                        </div>
                                    </button>
                                </li>
                                <li v-for="allergen in allergens" :key="allergen.id"><button type="button" v-on:click="goToFilters" class="btn btn-link p-0">
                                        <div class="allergens">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48">
                                                <path fill="none" d="M0 0h24v24H0z" />
                                                <path d="M23 5.5V20c0 2.2-1.8 4-4 4h-7.3c-1.08 0-2.1-.43-2.85-1.19L1 14.83s1.26-1.23 1.3-1.25c.22-.19.49-.29.79-.29.22 0 .42.06.6.16.04.01 4.31 2.46 4.31 2.46V4c0-.83.67-1.5 1.5-1.5S11 3.17 11 4v7h1V1.5c0-.83.67-1.5 1.5-1.5S15 .67 15 1.5V11h1V2.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V11h1V5.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5z" />
                                            </svg>
                                            {{ allergen.name }}
                                        </div>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <banner :location="`section-${menuType}-top`"></banner>

                <template v-for="(item, index) in items">
                    <h3 :class="`${menuType}-section-title section-title text-truncate cursor-pointer ${index == 0 ? 'mt-1' : ''}`" v-if="index === 0 || items[index - 1].category_name !== item.category_name" :key="`section-${index}`" :id="`section-${item.section_id}`" :data-section-id="item.section_id">
                        {{ item.category_name }}
                    </h3>

                    <router-link :to="`/${siteSlug}/${menuType}/${item.section_id}/${item.id}`" :class="`${menuType}-item menu-item no-underline ${item.current_stock == 0 ? `${menuType}-not-available menu-item-not-available` : ''}`" :key="`menu-item-${index}`" @click.native="outOfStock(item.current_stock)">
                        <div class="card card-item">
                            <div :class="`card-body card-body-${menuType} row`">
                                <div class="col">
                                    <div class="float-left h-100 bg-mc font-weight-bolder d-flex align-items-center mr-1 px-1" v-if="cartMenuItems.map(i => i.id).includes(item.id)">
                                        <small class="text-white">
                                            {{ cartMenuItems.find(i => i.id === item.id).qty }}
                                        </small>
                                    </div>

                                    <div class="item-header">
                                        <router-link :class="`${menuType}-item-title`" class="item-title" :to="`/${siteSlug}/${menuType}/${item.section_id}/${item.id}`">
                                            {{ item.name }}
                                        </router-link>
                                    </div>

                                    <div :class="`${menuType}-item-description item-description`">
                                        <p>{{ item.short_description }}</p>
                                    </div>

                                    <div class="d-flex justify-content-between mt-1">
                                        <div :class="`${menuType}-item-price item-price font-weight-bold`">
                                            <span v-if="item.price > 0">{{ item.price | moneyFormat }}</span>
                                        </div>
                                        <div class="item-calories text-muted font-italic" v-if="item.calories_amount">{{ $t("from") }} {{ item.calories_amount }} {{ $t("kcal") }}</div>
                                    </div>
                                </div>

                                <div class="col-auto d-flex align-items-center" v-if="item.has_image">
                                    <div class="image-wrapper image-loading">
                                        <img :data-src="`/images/menu-item/${item.id}?width=75&height=75&fit=crop&t=${currentTime}`" alt="Section image" style="min-height: 75px" class="lazyload card-img-top" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </router-link>
                </template>

                <banner :location="`section-${menuType}-bottom`"></banner>
            </div>
        </section>

        <modal name="out-of-stock-modal" width="100%" height="auto">
            <div class="p-3">
                <div class="text-center mt-3">
                    <h3 id="modal-headline">
                        {{ $t("Please note") }}
                    </h3>
                    <div class="mt-3">
                        <p class="text-danger">
                            {{ $t("The product is currently out of stock.") }}
                        </p>
                    </div>
                </div>
            </div>

            <hr>

            <div class="flex justify-content-center border-solid mt-4 pt-1 mb-4">
                <button class="btn btn-primary mr-2" v-on:click="$modal.hide('out-of-stock-modal')">{{ $t("Close") }}</button>
            </div>
        </modal>
    </div>
</template>

<script>
import Cart from "../../components/Cart";
import Navbar from "../../components/Navbar";
import Banner from '../../components/Banner';
import SectionSelector from '../../components/SectionSelector';

export default {
    components: { Cart, Navbar, Banner, SectionSelector },
    data() {
        return {
            items: [],
            sections: [],
            sectionId: parseInt(this.$route.query.section_id) || 0,
            siteSlug: this.$route.params.site,
            menuType: this.$route.params.menu,
            cart: window.app.cart,
            allergens: [],
            tags: [],
            menus: [],
            inactiveMessage: '',
            variants: window.app.variants,
            clickAndCollect: window.app.click_and_collect,
            hasFoodSection: false,
            hasDrinkSection: false,
            activeSectionId: null,
            currentTime: new Date().getTime()
        };
    },
    watch: {
        $route(newValue, oldValue) {
            this.mount();

            if (this.$route.query.section_id && this.$route.query.section_id != this.activeSectionId) {
                this.$nextTick(() => {
                    this.goToSection(this.$route.query.section_id);
                });
            }

            if (newValue.path != oldValue.path) {
                this.menuType = this.$route.params.menu;
            }
        }
    },
    computed: {
        activeFilters() {
            return this.allergens.length + this.tags.length;
        },
        cartMenuItems() {
            if (!this.cart || !this.cart.cart_menu_items.length) {
                return [];
            }

            const items = this.cart.cart_menu_items.map(m => {
                return {
                    id: m.menu_item_id,
                    qty: m.quantity
                }
            });

            const summedItems = [];

            items.forEach(i => {
                if (summedItems.filter(s => s.id == i.id).length === 0) {
                    summedItems.push({ id: i.id, qty: 0 })
                }

                const item = summedItems.find(s => s.id == i.id);
                item.qty += i.qty;
            });

            return summedItems;
        }
    },
    methods: {
        handleInput(sectionId) {
            this.sectionId = sectionId;
            this.$router.replace({ query: { section_id: this.sectionId } })

            this.fetchItems();
        },
        fetchItems() {
            this.$axios
                .get(`/items`, {
                    params: {
                        site_slug: this.$route.params.site,
                        menu_type: this.$route.params.menu,
                        allergens: this.allergens.map(a => a.id),
                        tags: this.tags.map(t => t.id)
                    }
                })
                .then(response => {
                    this.items = response.data.items;
                    this.sections = [];

                    this.items.forEach(i => {
                        if (!this.sections.map(s => s.id).includes(i.section_id)) {
                            this.sections.push({ id: i.section_id, name: i.category_name });
                        }
                    })

                    this.moveToCurrentSectionId();

                    if (!this.activeSectionId || !this.items.find(s => s.section_id == this.activeSectionId)) {
                        this.activeSectionId = this.items.length ? this.items[0].section_id : null;
                    }

                })
                .catch(error => {
                    this.$router.replace(`/error/${error.response.status}`);
                });
        },
        fetchMenus() {
            this.$axios
                .get(`/menus?site_slug=${this.siteSlug}`)
                .then(response => {
                    this.menus = response.data.items;

                    this.menus.forEach(m => {
                        if (m.type == 'food') {
                            this.hasFoodSection = true;
                        }

                        if (m.type == 'drink') {
                            this.hasDrinkSection = true;
                        }
                    })

                    this.inactiveMessage = response.data.inactive_message;
                })
                .catch(error => {
                    this.$router.replace(`/error/${error.response.status}`);
                });
        },
        getFiltersFromQueryParams() {
            let allergens = (this.$route.query.allergens || '').split(',');

            if (allergens.length == 1 && allergens[0] == '') {
                allergens = [];
            }

            let tags = (this.$route.query.tags || '').split(',');

            if (tags.length == 1 && tags[0] == '') {
                tags = [];
            }

            return { allergens, tags };
        },
        fetchFilters() {
            let filters = this.getFiltersFromQueryParams();

            if (!filters.allergens.length && !filters.allergens.length && !filters.tags.length && !filters.tags.length) {
                return this.fetchItems();
            }

            this.$axios
                .get('/filters', {
                    params: {
                        filters: filters
                    }
                })
                .then(response => {
                    this.allergens = response.data.allergens;
                    this.tags = response.data.tags;

                    this.fetchItems();
                });
        },
        removeTag(tag_id) {
            this.tags = this.tags.filter(t => t.id != tag_id);

            const query = Object.assign({}, this.$route.query);
            query.tags = this.tags.map(t => { return t.id }).toString();

            this.$router.push({ query });

            this.fetchItems();
        },
        removeAllergen(allergen_id) {
            this.allergens = this.allergens.filter(a => a.id != allergen_id);

            const query = Object.assign({}, this.$route.query);
            query.allergens = this.allergens.map(a => { return a.id }).toString();

            this.$router.push({ query });

            this.fetchItems();
        },
        goToFilters() {
            this.$router.push({ path: `/${this.siteSlug}/${this.menuType}/filters`, query: this.$route.query });
        },
        mount() {
            this.fetchFilters();
            this.fetchMenus();

            window.scrollTo({ top: 0, behavior: 'smooth' });

            this.siteSlug = this.$route.params.site;
            this.menuType = this.$route.params.menu;
            this.sectionId = parseInt(this.$route.query.section_id) || 0;

            this.$pageTitle = "Items";

            if (this.$route.query.add) {
                let query = Object.assign({}, this.$route.query);
                delete query.add;
                this.$router.replace({ query });

                this.$toasted.success(this.$t('The item has been added to the cart.'), { duration: 3000, position: 'bottom-center' });
            }
        },
        outOfStock(stock) {
            if (stock <= 0) {
                this.$modal.show('out-of-stock-modal');
            }
        },
        resetLayout() {
            const scale = 'scale(1)';

            document.body.style.webkitTransform = scale;    // Chrome, Opera, Safari
            document.body.style.msTransform = scale;       // IE 9
            document.body.style.transform = scale;     // General
        },
        moveToCurrentSectionId() {
            const sectionId = this.$route.query.section_id;

            if (!sectionId) {
                return;
            }

            const exists = !!this.items.find(s => s.section_id == sectionId);

            if (!exists) {
                return;
            }

            setTimeout(() => {
                this.goToSection(sectionId);
            }, 300);
        },
        goToSection(sectionId) {
            if (sectionId != this.activeSectionId) {
                this.activeSectionId = sectionId;
            }

            const el = window.document.getElementById(`section-${sectionId}`);

            if (!el) {
                return;
            }

            window.scroll({
                top: el.offsetTop - 200,
                behavior: 'smooth'
            });
        },
        setCurrentSection() {
            const currentScroll = window.scrollY;

            let minDifference = 99999;

            const sections = window.document.getElementsByClassName('section-title');

            for (let i = 0; i < sections.length; i++) {
                let diff = Math.abs(currentScroll - sections.item(i).offsetTop + 185);

                if (diff < minDifference) {
                    minDifference = diff;
                    this.activeSectionId = sections.item(i).dataset.sectionId;
                }
            }
        },
        goToMenu(menu) {
            this.$router.push(`/${this.siteSlug}/${menu}`);
        },
        async updateSectionIdQueryParam() {
            const query = Object.assign({}, this.$route.query);
            query.section_id = this.activeSectionId;
            await this.$router.push({ query });
        },
    },
    mounted() {
        this.mount();

        window.document.addEventListener('scroll', () => this.setCurrentSection());
    }
};
</script>
